import { useSelector } from "react-redux";
import { useThunkDispatch } from "setup/redux/thunkDispatch";
import {
  getBudgetHeadersForClientsBudget,
  selectBudgetHeaders,
  selectLoadingBudgetHeaders,
} from "./ClientBudgetHeadersTableSlice";
import { createColumnHelper } from "@tanstack/react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate, useParams } from "react-router-dom";
import { faDiagramCells, faArrowRight } from "@fortawesome/pro-solid-svg-icons";
import { useEffect } from "react";
import CPLTable from "cpl-react-common/components/Tables/CPLTable";
import { ActivityIndicator } from "cpl-react-common/components/ActivityIndicators/ActivityIndicator";
import { ClientPortalBudgetHeaderModel } from "services/api/cplapi/CPLAPI";

const ClientTransactionsTable: React.FC = () => {
  const { clientID, budgetID } = useParams();
  const thunkDispatch = useThunkDispatch();
  const navigate = useNavigate();
  const budgetHeaders = useSelector(selectBudgetHeaders);
  const isLoading = useSelector(selectLoadingBudgetHeaders);

  const columnHelper = createColumnHelper<ClientPortalBudgetHeaderModel>();
  const columnDefinition = [
    columnHelper.display({
      id: "icon",
      cell: () => (
        <div className="d-flex">
          <FontAwesomeIcon icon={faDiagramCells} size="lg" className="cpl-brand-primary" />
        </div>
      ),
      enableHiding: false,
    }),
    columnHelper.accessor((row) => row.header, {
      id: "Budget Header",
      cell: (info) => info.getValue(),
      header: () => <>Budget Header</>,
      enableColumnFilter: false,
    }),
    columnHelper.accessor((row) => row.annualBudget, {
      id: "Annual Budget",
      cell: (info) => info.getValue()?.toGBPString(),
      header: () => <>Annual Budget</>,
      enableColumnFilter: false,
      meta: {
        columnClassName: "currency-column-header",
        dataClassName: "text-end",
      },
    }),
    columnHelper.accessor((row) => row.actualToDate, {
      id: "Actual To Date",
      cell: (info) => info.getValue()?.toGBPString(),
      header: () => <>Actual To Date</>,
      enableColumnFilter: false,
      meta: {
        columnClassName: "currency-column-header",
        dataClassName: "text-end",
      },
    }),
    columnHelper.display({
      id: "actions",
      cell: (info) => (
        <>
          {info.row.original.headerID && (
            <div
              data-test-id={`budget-headers-table__view-charges-link-${info.row.original.headerID}`}
              className="cpl-btn btn-sm cpl-btn-outline"
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              title="View Charges"
              onClick={() => {
                navigateToCharges(info.row.original);
              }}
            >
              <FontAwesomeIcon icon={faArrowRight} size="lg" />
            </div>
          )}
        </>
      ),
      enableHiding: false,
    }),
  ];

  useEffect(() => {
    thunkDispatch(getBudgetHeadersForClientsBudget({ clientID: clientID!, budgetID: budgetID! }));
  }, [thunkDispatch, clientID, budgetID]);

  const navigateToCharges = (row: ClientPortalBudgetHeaderModel) => {
    const path = `/ClientBudgets/${clientID}/${row.budgetID}/${row.headerID}`;
    navigate(path);
  };

  return (
    <>
      <div className="card d-flex">
        <ActivityIndicator isBusy={isLoading}>
          <div className="card-body">
            <CPLTable
              data={budgetHeaders.budgetHeaders ?? []}
              columns={columnDefinition}
              rowMobileClickAction={(row) => navigateToCharges(row)}
              sortingState={[{ id: "Budget Header", desc: false }]}
              classNames="cpl-table-nowrap-below-desktop"
            />
          </div>
        </ActivityIndicator>
      </div>
    </>
  );
};

export default ClientTransactionsTable;
